// Implements: F-ITK-007
/**
 * The `intake` module's client for the WhatsApp linking routes, on top of the single HTTP exit.
 *
 * Issuing a code and removing a binding are sent with `retry: false` (checklist 13): neither is
 * paid, but a retried issue after a lost answer would revoke the code the person may already be
 * typing on the phone, and a removal is the person's decision, made once.
 *
 * **A listed binding keeps only the fields the client shows** (`toBinding`): if a server ever
 * sent the number in clear under another field, it would be dropped here, before the answer
 * reaches the query cache or the DOM. The issued code is handed back to the caller only.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type {
  IssueLinkCodeInput,
  IssuedLinkCodeView,
  SenderBindingListView,
  SenderBindingView,
} from "../../../types/intake";

export const LINK_CODES_PATH = `${API_BASE}/intake/link-codes`;
export const BINDINGS_PATH = `${API_BASE}/intake/bindings`;

/** A listed binding, field by field: whatever else the payload carries is not kept. */
export function toBinding(raw: SenderBindingView): SenderBindingView {
  return {
    id: raw.id,
    channel: raw.channel,
    masked_address: raw.masked_address,
    scope_kind: raw.scope_kind,
    scope_id: raw.scope_id,
    organization_id: raw.organization_id,
    created_at: raw.created_at,
  };
}

export async function fetchBindings(signal?: AbortSignal): Promise<readonly SenderBindingView[]> {
  const body = await httpRequest<Partial<SenderBindingListView>>(BINDINGS_PATH, { signal });
  return (body?.bindings ?? []).map(toBinding);
}

export async function issueLinkCode(input: IssueLinkCodeInput): Promise<IssuedLinkCodeView> {
  const body = await httpRequest<IssuedLinkCodeView>(LINK_CODES_PATH, { method: "POST", body: input, retry: false });
  if (body === undefined) {
    throw new ApiError(201, UNKNOWN_ERROR_CODE, "POST /intake/link-codes answered with no body", new Headers());
  }
  return { code: body.code, command: body.command, expires_at: body.expires_at };
}

export async function unlinkBinding(bindingId: string): Promise<void> {
  await httpRequest<unknown>(`${BINDINGS_PATH}/${encodeURIComponent(bindingId)}`, {
    method: "DELETE",
    retry: false,
  });
}
